import { LockKey } from 'phosphor-react';
import { useAppSelector } from '../../../../redux/store';

/**
 * Whether cash application is switched on for the current supplier.
 *
 * Opt-in: unset means off, matching the backend, which reads `cashAppEnabled === true` before
 * syncing anything. A hook rather than a repeated selector so the two screens that gate on it
 * cannot drift into disagreeing about what "off" means.
 */
export const useCashAppEnabled = (): boolean =>
  useAppSelector((state) => state.auth.currentSupplier?.cashAppEnabled === true);

/**
 * Shown at the top of the configuration screens when the feature is off.
 *
 * The screens stay reachable and their controls stay visible but disabled, rather than the
 * navigation hiding them. Someone evaluating Dill should be able to see what connecting a bank
 * or a remittance mailbox would involve before asking for it, and an admin looking for the
 * setting should find the page where they expect it rather than nothing at all.
 *
 * There is no self-serve switch yet — the flag is set on the supplier record — so the banner
 * says who to ask instead of offering a button that does not exist.
 */
export const CashAppLockedBanner = () => (
  <div
    role="status"
    className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 flex-shrink-0">
      <LockKey size={18} weight="duotone" className="text-amber-700" />
    </div>
    <div>
      <p className="text-sm font-medium text-amber-900">AI Cash Application is not enabled</p>
      <p className="text-xs text-amber-700 mt-0.5">
        These settings are read-only until the feature is switched on for your account. Nothing is
        ingested, matched or posted while it is off. Contact your Dill representative to enable it.
      </p>
    </div>
  </div>
);

/**
 * Dims a card whose controls are disabled.
 *
 * Deliberately visual only — no `pointer-events-none`. Blocking pointer events would also stop
 * text selection and hide the not-allowed cursor, and it does nothing for keyboard users, who
 * would still tab into the controls. Each control is disabled individually instead, which takes
 * it out of the tab order for free.
 */
export const lockedCardClass = (locked: boolean): string => (locked ? ' opacity-60' : '');
